/* Every Bit Organic — storefront interaction layer */
document.addEventListener("DOMContentLoaded", () => {
  initMobileMenu();
  initHeroSlideshow();
  initHeaderSearchOverlay();
  initShopFilters();
  initQtyStepper();
  initVariantSelect();
  initProductGallery();
});

function initMobileMenu() {
  const button = document.querySelector(".mobile-menu-button");
  const nav = document.querySelector(".left-nav");
  if (!button || !nav) return;

  const close = () => {
    nav.classList.remove("mobile-open");
    button.setAttribute("aria-expanded", "false");
    button.setAttribute("aria-label", "Open menu");
    document.body.classList.remove("mobile-nav-open");
  };

  button.addEventListener("click", () => {
    const opening = !nav.classList.contains("mobile-open");
    if (opening) {
      nav.classList.add("mobile-open");
      button.setAttribute("aria-expanded", "true");
      button.setAttribute("aria-label", "Close menu");
      document.body.classList.add("mobile-nav-open");
    } else {
      close();
    }
  });

  nav.addEventListener("click", (event) => {
    if (event.target.closest("a") && window.matchMedia("(max-width: 860px)").matches) close();
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && nav.classList.contains("mobile-open")) {
      close();
      button.focus();
    }
  });

  window.addEventListener("resize", () => {
    if (window.innerWidth > 860) close();
  });
}

function initHeaderSearchOverlay() {
  const trigger = document.getElementById("headerSearchTrigger");
  const overlay = document.getElementById("headerSearchOverlay");
  if (!trigger || !overlay) return;

  const input = overlay.querySelector("input[name='q']");
  const closeButton = overlay.querySelector(".search-overlay-close");
  let lastFocused = null;

  const focusable = () => Array.from(
    overlay.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')
  );

  const open = () => {
    lastFocused = document.activeElement;
    overlay.classList.add("open");
    overlay.setAttribute("aria-hidden", "false");
    trigger.setAttribute("aria-expanded", "true");
    document.body.classList.add("no-scroll");
    window.setTimeout(() => input?.focus(), 30);
  };

  const close = () => {
    if (!overlay.classList.contains("open")) return;
    overlay.classList.remove("open");
    overlay.setAttribute("aria-hidden", "true");
    trigger.setAttribute("aria-expanded", "false");
    document.body.classList.remove("no-scroll");
    if (lastFocused && typeof lastFocused.focus === "function") lastFocused.focus();
  };

  trigger.addEventListener("click", open);
  closeButton?.addEventListener("click", close);
  overlay.addEventListener("click", (event) => {
    if (event.target === overlay) close();
  });

  overlay.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== "Tab") return;
    const items = focusable();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
}

const HERO_SLIDE_COPY = [
  {
    headline: "Cold-Pressed<br><em>Organic Oils</em>",
    description: "Explore EBO's certified organic food-oil range for everyday cooking and dressing."
  },
  {
    headline: "Activated<br><em>Nuts &amp; Seeds</em>",
    description: "Discover the Live Wholefoods activated range, from nuts and seeds to muesli and granola."
  },
  {
    headline: "Nut Butters<br><em>&amp; Spreads</em>",
    description: "Shop Crack'd and Live Wholefoods nut butters and spreads from the approved EBO range."
  },
  {
    headline: "Skin, Body<br><em>&amp; Hair</em>",
    description: "Explore botanical oils and care products across the EBO skin, body and hair range."
  },
  {
    headline: "Organic<br><em>Pantry</em>",
    description: "Beans, tomatoes, passata, honey, vinegars, salts and other everyday pantry products."
  },
  {
    headline: "Home<br><em>&amp; Cleaning</em>",
    description: "Explore the EBO plant-based enzyme cleaning range for everyday home care."
  }
];

function initHeroSlideshow() {
  const container = document.getElementById("heroSlides");
  const headline = document.getElementById("heroHeadline");
  const description = document.getElementById("heroDescription");
  const pauseButton = document.getElementById("heroPause");
  if (!container) return;

  const slides = Array.from(container.querySelectorAll(".hero-slide"));
  if (slides.length <= 1) {
    if (pauseButton) pauseButton.hidden = true;
    return;
  }

  let current = 0;
  let timer = null;
  let paused = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const intervalMs = 6500;

  const setPauseUI = () => {
    if (!pauseButton) return;
    pauseButton.setAttribute("aria-pressed", paused ? "true" : "false");
    pauseButton.setAttribute("aria-label", paused ? "Play hero slideshow" : "Pause hero slideshow");
    const text = pauseButton.querySelector(".hero-pause-text");
    const icon = pauseButton.querySelector(".hero-pause-icon");
    if (text) text.textContent = paused ? "Play slideshow" : "Pause slideshow";
    if (icon) icon.textContent = paused ? "▶" : "Ⅱ";
  };

  const show = (index) => {
    slides[current].classList.remove("active");
    current = index;
    slides[current].classList.add("active");

    const copy = HERO_SLIDE_COPY[current];
    if (!copy || !headline || !description) return;
    headline.classList.add("hero-text-fade");
    description.classList.add("hero-text-fade");
    window.setTimeout(() => {
      headline.innerHTML = copy.headline;
      description.textContent = copy.description;
      headline.classList.remove("hero-text-fade");
      description.classList.remove("hero-text-fade");
    }, 220);
  };

  const stop = () => {
    if (timer) window.clearInterval(timer);
    timer = null;
  };

  const start = () => {
    stop();
    if (paused) return;
    timer = window.setInterval(() => show((current + 1) % slides.length), intervalMs);
  };

  pauseButton?.addEventListener("click", () => {
    paused = !paused;
    setPauseUI();
    start();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) stop();
    else start();
  });

  setPauseUI();
  start();
}

function initShopFilters() {
  const grid = document.getElementById("productGrid");
  if (!grid) return;

  const countLabel = document.getElementById("productCount");
  const searchInput = document.getElementById("productSearch");
  const sortSelect = document.getElementById("sortProducts");
  const filterButtons = Array.from(document.querySelectorAll(".filter-button"));
  let activeCategory = "all";

  const params = new URLSearchParams(window.location.search);
  const urlCategory = params.get("category");
  if (urlCategory && filterButtons.some((button) => button.dataset.category === urlCategory)) {
    activeCategory = urlCategory;
  }

  const urlSearch = params.get("search");
  if (urlSearch && searchInput) searchInput.value = urlSearch;

  const updateButtons = () => {
    filterButtons.forEach((button) => {
      const active = button.dataset.category === activeCategory;
      button.classList.toggle("active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
    });
  };

  const render = () => {
    const query = (searchInput?.value || "").trim().toLocaleLowerCase();
    const sortBy = sortSelect?.value || "featured";
    const cards = Array.from(grid.querySelectorAll(".product-card"));
    let visibleCount = 0;

    cards.forEach((card) => {
      const matchesCategory = activeCategory === "all" || card.dataset.category === activeCategory;
      const matchesQuery = !query || card.textContent.toLocaleLowerCase().includes(query);
      const visible = matchesCategory && matchesQuery;
      card.hidden = !visible;
      if (visible) visibleCount += 1;
    });

    if (sortBy !== "featured") {
      const visibleCards = cards.filter((card) => !card.hidden);
      visibleCards.sort((a, b) => {
        if (sortBy === "price-low") return Number(a.dataset.priceCents || 0) - Number(b.dataset.priceCents || 0);
        if (sortBy === "price-high") return Number(b.dataset.priceCents || 0) - Number(a.dataset.priceCents || 0);
        if (sortBy === "name") {
          const aName = a.querySelector("h3")?.textContent?.trim() || "";
          const bName = b.querySelector("h3")?.textContent?.trim() || "";
          return aName.localeCompare(bName);
        }
        return 0;
      });
      visibleCards.forEach((card) => grid.appendChild(card));
    }

    if (countLabel) {
      countLabel.textContent = `Showing ${visibleCount} approved product${visibleCount === 1 ? "" : "s"}`;
    }
  };

  filterButtons.forEach((button) => {
    button.addEventListener("click", () => {
      activeCategory = button.dataset.category || "all";
      updateButtons();
      render();

      const url = new URL(window.location.href);
      if (activeCategory === "all") url.searchParams.delete("category");
      else url.searchParams.set("category", activeCategory);
      history.replaceState({}, "", url);
    });
  });

  searchInput?.addEventListener("input", render);
  sortSelect?.addEventListener("change", render);

  updateButtons();
  render();
}

function initQtyStepper() {
  document.querySelectorAll(".qty-selector").forEach((selector) => {
    const input = selector.querySelector(".qty-input");
    const decrease = selector.querySelector(".qty-dec");
    const increase = selector.querySelector(".qty-inc");
    if (!input) return;

    decrease?.addEventListener("click", () => {
      const min = Number(input.min || 1);
      input.value = String(Math.max(min, (parseInt(input.value, 10) || min) - 1));
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });

    increase?.addEventListener("click", () => {
      input.value = String((parseInt(input.value, 10) || 0) + 1);
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  });
}

function initVariantSelect() {
  const select = document.getElementById("variantSelect");
  const hiddenId = document.getElementById("selectedVariantId");
  if (!select || !hiddenId) return;

  const submit = document.querySelector(".product-detail-add");
  const price = document.getElementById("ProductPrice");
  const compare = document.getElementById("ProductComparePrice");
  const sku = document.getElementById("ProductSku");
  const barcode = document.getElementById("ProductBarcode");
  const skuRow = document.getElementById("ProductSkuRow") || sku?.parentElement;
  const barcodeRow = document.getElementById("ProductBarcodeRow") || barcode?.parentElement;
  const mainImage = document.getElementById("ProductMainImage");

  const sync = () => {
    const option = select.options[select.selectedIndex];
    if (!option) return;

    hiddenId.value = option.value;
    const soldOut = option.disabled;

    if (submit) {
      submit.disabled = soldOut;
      submit.textContent = soldOut ? "Sold Out" : "Add to Cart";
    }
    if (price && option.dataset.price) price.textContent = option.dataset.price;

    if (compare) {
      const value = option.dataset.comparePrice || "";
      compare.textContent = value;
      compare.hidden = !value;
    }

    if (sku) sku.textContent = option.dataset.sku || "";
    if (skuRow) skuRow.hidden = !option.dataset.sku;

    if (barcode) barcode.textContent = option.dataset.barcode || "";
    if (barcodeRow) barcodeRow.hidden = !option.dataset.barcode;

    if (mainImage && option.dataset.image) {
      mainImage.src = option.dataset.image;
      mainImage.removeAttribute("srcset");
      document.querySelectorAll(".product-thumbnail").forEach((button) => {
        const active = button.dataset.galleryImage === option.dataset.image;
        button.classList.toggle("active", active);
        button.setAttribute("aria-pressed", active ? "true" : "false");
      });
    }
  };

  select.addEventListener("change", sync);
  sync();
}

function initProductGallery() {
  const mainImage = document.getElementById("ProductMainImage");
  if (!mainImage) return;

  document.querySelectorAll(".product-thumbnail").forEach((button) => {
    button.addEventListener("click", () => {
      const src = button.dataset.galleryImage;
      if (!src) return;
      mainImage.src = src;
      mainImage.removeAttribute("srcset");
      mainImage.alt = button.dataset.galleryAlt || mainImage.alt;

      document.querySelectorAll(".product-thumbnail").forEach((candidate) => {
        const active = candidate === button;
        candidate.classList.toggle("active", active);
        candidate.setAttribute("aria-pressed", active ? "true" : "false");
      });
    });
  });
}
